import { useEffect, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { m } from 'motion/react'
import { Button, Price } from '@/components/ui'
import { FugueContactForm } from '@/components/FugueContactForm'
import { PricingConfigurator } from '@/components/PricingConfigurator'
import { useSEO } from '@/hooks/useSEO'
import { useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { trackPricingView } from '@/lib/analytics'
import {
  CATALOG_RULE_LINE,
  CAPABILITIES,
  CONSULTING,
  CREATIVE_LEAD,
  DESIGN_PROJECTS,
  DFY_BUNDLES,
  FESTIVAL_STACK_IDS,
  FESTIVAL_STACK_MONTHLY,
  MANAGED_WEBSITE,
  ONE_TIME_AND_ADDONS,
  OTHER_MANAGED_SERVICES,
  PHOTOGRAPHY_PROJECTS,
  PHOTOGRAPHY_SERVICE_AREA,
  SELF_SERVE_SPLIT_LINE,
  TEAM_RUN_CAPABILITIES,
  capabilityById,
  capabilityNameList,
  dfyBundle,
  type Capability,
  type PriceBundle,
} from '@gjl/voice/pricing'

// Pricing data is single-sourced from @gjl/voice/pricing. Edit prices there.

// Catalog ids/names are internal; customer-facing labels can differ.
const DISPLAY_LABELS: Record<string, string> = {
  'bespoke-build': 'Custom build',
}

const labelFor = (item: { id: string; name: string }) => DISPLAY_LABELS[item.id] ?? item.name

// Festival lane prices render from the pricing module (never hardcode). The
// $500/mo Festival Platform bundle was removed 2026-07-29: festivals now buy
// the same capabilities everyone else does, and Concierge is the only
// done-for-you festival option.
const conciergeBundle = dfyBundle('concierge')
const festivalStack = FESTIVAL_STACK_IDS.map(capabilityById)

// The four small conveniences render as fine print, not as price-list rows.
const CARD_BUILDS = new Set(['launch-build', 'bespoke-build'])

const linkFocus = 'focus-visible:outline-primary focus-visible:outline-2 focus-visible:outline-offset-4'

// Every catalogue entry is one row of a price list: name in the display face,
// the price in <Price>'s mono, the note in muted body text. These are a la
// carte items, not side-by-side plans, so they get no box, border, or hover
// lift; space and type separate them.
function PriceItem({ name, price, children }: { name: string; price: ReactNode; children?: ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="font-display text-xl leading-tight md:text-2xl">{name}</h3>
        {price}
      </div>
      {children}
    </div>
  )
}

function CapabilityItem({ cap }: { cap: Capability }) {
  return (
    <PriceItem name={cap.name} price={<Price size="md">${cap.priceMonthly}/mo</Price>}>
      {cap.perUnitFee && <p className="text-muted-foreground text-sm">+ {cap.perUnitFee}</p>}
      <p className="text-muted-foreground max-w-[48ch] text-sm leading-relaxed">{cap.note}</p>
      <p className="text-sm text-white/80">
        {cap.selfServeLive ? 'Run it yourself today' : 'Our team sets it up and runs it for you today'}
      </p>
    </PriceItem>
  )
}

function BundleItem({ bundle }: { bundle: PriceBundle }) {
  return (
    <PriceItem name={labelFor(bundle)} price={<Price size="md">{bundle.price}</Price>}>
      <p className="text-muted-foreground max-w-[52ch] text-sm leading-relaxed">{bundle.note}</p>
    </PriceItem>
  )
}

// Section header on the home page's 5/7 split: the heading left, its lede right.
function SectionHead({ id, title, children }: { id: string; title: string; children?: ReactNode }) {
  return (
    <div className="grid gap-4 md:grid-cols-12 md:gap-10">
      <h2 id={id} className="text-fluid-h3 md:col-span-5">
        {title}
      </h2>
      {children && (
        <div className="text-muted-foreground text-fluid-body max-w-[60ch] leading-relaxed md:col-span-7">
          {children}
        </div>
      )}
    </div>
  )
}

export default function PackagesPage() {
  useEffect(() => {
    trackPricingView('/pricing')
  }, [])
  // SPA navigation doesn't trigger native anchor scrolling (e.g. /packages#consulting),
  // including hash-only changes while already on this page.
  const location = useLocation()
  useEffect(() => {
    const id = location.hash.slice(1)
    if (!id) return
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }, [location])
  const breadcrumbs = generateBreadcrumbs('/pricing', breadcrumbLabels)

  // Hero entrance only; the per-grid staggered reveals went with the cards.
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)

  useSEO({
    title: 'Pricing',
    description:
      'Pay for the capabilities you turn on: website, applications, email, donor management, ticketing, and music planning. Run them yourself, or we run them for you.',
    url: '/pricing',
    canonical: '/pricing',
    breadcrumbs,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'OfferCatalog',
      name: 'Fugue Lab Pricing',
      description: 'One operating system for music organizations, priced by capability: self-serve and done-for-you.',
      provider: {
        '@type': 'Organization',
        name: 'Fugue Lab',
        url: 'https://fuguelab.com',
      },
    },
  })

  return (
    <div className="mx-auto space-y-28 pb-4 md:space-y-36">
      {/* Hero */}
      <section className="relative min-h-[80vh] w-full overflow-hidden md:h-[70vh]">
        {/* T0 hero treatment — see ServicesPage for the full rationale. */}
        <img
          src="/servers.jpg"
          alt="The Fugue Lab operating system for music organizations"
          className="ken-burns absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/80" />
        <div className="fx-vignette" />
        <div className="fx-grain" />
        <div
          ref={heroRef}
          className="relative z-10 mx-auto flex min-h-[80vh] max-w-5xl flex-col items-center justify-center gap-y-6 px-5 text-center text-white"
        >
          <h1 className="text-4xl font-bold tracking-tight">Everything your music organization needs online</h1>
          <p className="max-w-2xl text-lg leading-relaxed text-white/90">
            Website, applications, email, donations, ticketing, and music planning on one system. Turn on what you need.
            Run it yourself, or have us run it for you.
          </p>
          <ul className="mt-2 space-y-2 text-sm text-white/70">
            <li>Self-serve from $25/mo. Done-for-you from $100/mo.</li>
            <li>Accepting donations is always free: 0% platform fee</li>
            <li>No vendor lock-in. Leave any time with all of your data, free.</li>
          </ul>
          <m.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <a href="/book" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="mt-4 cursor-pointer">
                Book a Free Call →
              </Button>
            </a>
          </m.div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl space-y-28 px-5 md:space-y-36 md:px-12">
        {/* Two-axis explainer */}
        <section aria-labelledby="two-choices">
          <SectionHead id="two-choices" title="Two choices, one system">
            <p>
              Every organization makes two decisions: which capabilities to turn on, and who runs them. Start with a
              website, add email or giving when you are ready, and delegate any piece to us at any time. Everything runs
              on the same system, so upgrading never means migrating.
            </p>
          </SectionHead>
        </section>
      </div>

      {/* Interactive configurator */}
      <PricingConfigurator />

      <div className="mx-auto max-w-6xl space-y-28 px-5 md:space-y-36 md:px-12">
        {/* Capabilities a la carte */}
        <section aria-labelledby="capabilities" className="space-y-12">
          <SectionHead id="capabilities" title="The capabilities">
            <p>Each capability has its own monthly price. {SELF_SERVE_SPLIT_LINE}</p>
          </SectionHead>
          <div className="grid gap-x-12 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((cap) => (
              <CapabilityItem key={cap.id} cap={cap} />
            ))}
          </div>
          <p className="text-muted-foreground max-w-[70ch] text-sm leading-relaxed">
            Accepting donations is free forever: a Donate button with money going straight to your own Stripe account,
            0% platform fee. The Donor Management price is for the management layer: recurring giving, tax receipts, and
            donor records.
          </p>
        </section>

        {/* Catalog rule: a statement set large in the display face, not a band. */}
        <section aria-labelledby="pay-for-what" className="max-w-4xl space-y-5">
          <h2 id="pay-for-what" className="text-fluid-h3">
            Pay for what you turn on
          </h2>
          <p className="font-display text-primary text-[clamp(1.5rem,2.6vw,2.25rem)] leading-[1.25] text-balance">
            {CATALOG_RULE_LINE}
          </p>
          <p className="text-muted-foreground max-w-[70ch] text-sm leading-relaxed">
            Any mix counts, on one system, so upgrading never means migrating.{' '}
            {TEAM_RUN_CAPABILITIES.length > 0 &&
              `${capabilityNameList(TEAM_RUN_CAPABILITIES)} ${TEAM_RUN_CAPABILITIES.length === 1 ? 'is' : 'are'} set up and run for you by our team today.`}
          </p>
        </section>

        {/* Done for you */}
        <section aria-labelledby="done-for-you" className="space-y-12">
          <SectionHead id="done-for-you" title="Done for you">
            <p>
              We operate it and you stay in charge. Managed website and email include the underlying software, so you
              see one number.
            </p>
          </SectionHead>
          <div className="grid gap-x-12 gap-y-12 md:grid-cols-2 lg:grid-cols-4">
            {/* Managed Website is one service with two tiers (Care / Unlimited). */}
            <PriceItem
              name={MANAGED_WEBSITE.name}
              price={<Price size="md">From ${MANAGED_WEBSITE.tiers[0].priceMonthly}/mo</Price>}
            >
              <p className="text-muted-foreground text-sm leading-relaxed">{MANAGED_WEBSITE.blurb}</p>
              <ul className="space-y-3 pt-2 text-sm">
                {MANAGED_WEBSITE.tiers.map((tier) => (
                  <li key={tier.id} className="space-y-0.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-semibold text-white/90">{tier.label}</span>
                      <Price size="sm">${tier.priceMonthly}/mo</Price>
                    </div>
                    <p className="text-muted-foreground">{tier.note}</p>
                  </li>
                ))}
              </ul>
            </PriceItem>
            {OTHER_MANAGED_SERVICES.map((svc) => (
              <PriceItem key={svc.id} name={svc.name} price={<Price size="md">${svc.priceMonthly}/mo</Price>}>
                <p className="text-muted-foreground text-sm leading-relaxed">{svc.note}</p>
              </PriceItem>
            ))}
          </div>
          <div className="grid gap-x-12 gap-y-12 md:grid-cols-2">
            {DFY_BUNDLES.map((bundle) => (
              <BundleItem key={bundle.id} bundle={bundle} />
            ))}
          </div>
          <p className="text-muted-foreground max-w-[70ch] text-sm leading-relaxed">
            Done-for-you plans start with a one-time onboarding (a Launch build counts) and a 3-month minimum term.
            Fully Managed website requests are unlimited: one active at a time, 72-hour turnaround.
          </p>
        </section>
      </div>

      {/* Festival lane: the page's one change of ground, because it is a
          separate buying path rather than another row of the catalogue. */}
      <section aria-labelledby="for-festivals" className="bg-surface px-5 py-20 md:px-12 md:py-28">
        <div className="mx-auto grid max-w-6xl gap-10 md:grid-cols-12">
          <div className="space-y-5 md:col-span-5">
            <h2 id="for-festivals" className="text-fluid-h3 text-white">
              For festivals
            </h2>
            <p className="leading-relaxed text-white/80">
              Auditions and applications with no per-applicant fees, on the same system as your website. Turn on the
              five capabilities a festival actually uses and the whole stack is ${FESTIVAL_STACK_MONTHLY}/mo. Or have us
              design and run it for you at {conciergeBundle.price}.
            </p>
          </div>
          <div className="space-y-6 md:col-span-6 md:col-start-7">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <h3 className="font-display text-2xl text-white">The festival stack</h3>
              <Price size="lg">${FESTIVAL_STACK_MONTHLY}/mo</Price>
            </div>
            <ul className="space-y-3">
              {festivalStack.map((cap) => (
                <li key={cap.id} className="flex items-baseline justify-between gap-3 text-white/80">
                  <span>{cap.name}</span>
                  <Price size="sm">${cap.priceMonthly}/mo</Price>
                </li>
              ))}
            </ul>
            <p className="text-sm leading-relaxed text-white/60">
              Applications stands alone at ${capabilityById('applications').priceMonthly}/mo if that is all you need.
              Want the festival fully run, branding and launch included? That is Concierge, above.
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl space-y-28 px-5 md:space-y-36 md:px-12">
        {/* Consulting */}
        <section id="consulting" aria-labelledby="consulting-heading" className="scroll-mt-24 space-y-12">
          <SectionHead id="consulting-heading" title="Consulting">
            <p>
              Strategy and direction without full execution: done with you, above self-serve and distinct from
              done-for-you.
            </p>
          </SectionHead>
          <div className="grid gap-x-12 gap-y-12 md:grid-cols-2">
            {CONSULTING.map((bundle) => (
              <BundleItem key={bundle.id} bundle={bundle} />
            ))}
          </div>
        </section>

        {/* Graphic design */}
        <section id="design" aria-labelledby="design-heading" className="scroll-mt-24 space-y-12">
          <SectionHead id="design-heading" title="Graphic design">
            <p>
              Brand identity, posters, programs, and season campaigns as one-time projects, or the Managed Design
              retainer above for a designer on call.
            </p>
          </SectionHead>
          <div className="grid gap-x-12 gap-y-12 md:grid-cols-2">
            {DESIGN_PROJECTS.map((item) => (
              <BundleItem key={item.id} bundle={item} />
            ))}
          </div>
          <a
            href="/services/design"
            className={`text-primary inline-block text-sm font-medium underline decoration-1 underline-offset-4 hover:decoration-2 ${linkFocus}`}
          >
            More on graphic design
          </a>
        </section>

        {/* Photography */}
        <section id="photography" aria-labelledby="photography-heading" className="scroll-mt-24 space-y-12">
          <SectionHead id="photography-heading" title="Photography">
            <p>
              Concert coverage, headshots, and season photography days, delivered as edited galleries you own outright.{' '}
              {PHOTOGRAPHY_SERVICE_AREA}
            </p>
          </SectionHead>
          <div className="grid gap-x-12 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
            {PHOTOGRAPHY_PROJECTS.map((item) => (
              <BundleItem key={item.id} bundle={item} />
            ))}
          </div>
          <p className="text-muted-foreground max-w-[70ch] text-sm leading-relaxed">
            {CREATIVE_LEAD.credit}{' '}
            <a
              href="/services/photography"
              className={`text-primary underline decoration-1 underline-offset-4 hover:decoration-2 ${linkFocus}`}
            >
              More on photography
            </a>
            .
          </p>
        </section>

        {/* Builds + add-ons */}
        <section aria-labelledby="builds" className="space-y-12">
          <SectionHead id="builds" title="Builds and add-ons">
            <p>
              Get live with a one-time build, then add the small conveniences that keep everything running under one
              roof.
            </p>
          </SectionHead>
          <div className="grid gap-x-12 gap-y-12 md:grid-cols-2">
            {ONE_TIME_AND_ADDONS.filter((item) => CARD_BUILDS.has(item.id)).map((item) => (
              <BundleItem key={item.id} bundle={item} />
            ))}
          </div>
          <div className="max-w-[70ch] space-y-2">
            {ONE_TIME_AND_ADDONS.filter((item) => !CARD_BUILDS.has(item.id)).map((item) => (
              <p key={item.id} className="text-muted-foreground text-sm leading-relaxed">
                <span className="text-white/80">{labelFor(item)}</span> ({item.price}): {item.note}
              </p>
            ))}
            <p className="text-muted-foreground pt-4 text-sm leading-relaxed">
              Your domain is always registered in your name, and exporting your content, contacts, and donor data is
              free, always. You can leave with everything. That is the point.
            </p>
          </div>
        </section>

        {/* How it works. A real sequence, so it keeps its numbers, set as large
            muted figures in the display face, as on the home page. */}
        <section aria-labelledby="how-it-works" className="space-y-12">
          <h2 id="how-it-works" className="text-fluid-h3">
            How it works
          </h2>
          <ol className="grid gap-12 md:grid-cols-3 md:gap-10">
            <li className="space-y-3">
              <span aria-hidden="true" className="font-display text-muted-foreground/60 block text-6xl leading-none">
                1
              </span>
              <h3 className="text-xl font-semibold">Strategy Call</h3>
              <p className="text-muted-foreground max-w-[40ch] leading-relaxed">
                Free 15-minute call to understand your needs and pick the right capabilities.
              </p>
            </li>
            <li className="space-y-3">
              <span aria-hidden="true" className="font-display text-muted-foreground/60 block text-6xl leading-none">
                2
              </span>
              <h3 className="text-xl font-semibold">Build & Launch</h3>
              <p className="text-muted-foreground max-w-[40ch] leading-relaxed">
                A $250 Launch build gets you live: custom design, your content, domain connected.
              </p>
            </li>
            <li className="space-y-3">
              <span aria-hidden="true" className="font-display text-muted-foreground/60 block text-6xl leading-none">
                3
              </span>
              <h3 className="text-xl font-semibold">Grow</h3>
              <p className="text-muted-foreground max-w-[40ch] leading-relaxed">
                Turn on capabilities as you grow, and hand any of them to us whenever you want them off your plate.
              </p>
            </li>
          </ol>
          <p className="text-muted-foreground">Pay annually on any plan and get 2 months free.</p>
        </section>

        {/* Contact Form CTA */}
        <FugueContactForm />
      </div>
    </div>
  )
}
